import type { Profile } from "./types";

type ServiceAreaProfile = Pick<
  Profile,
  | "address"
  | "city"
  | "latitude"
  | "longitude"
  | "serviceAreas"
  | "state"
>;

export type ServiceAreaCoordinates = {
  lat: number;
  lng: number;
};

export type ServiceAreaSuggestion = {
  distanceKm: number;
  label: string;
  source: "curated" | "fallback";
};

type CuratedArea = {
  distanceKm?: number;
  label: string;
  lat?: number;
  lng?: number;
};

type CuratedCity = {
  areas: CuratedArea[];
  origin: ServiceAreaCoordinates;
};

type ServiceAreaSuggestionOptions = {
  limit?: number;
  query?: string;
  radiusKm?: number;
};

const normalizeText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const parseCityState = (profile: ServiceAreaProfile) => {
  const cityParts = (profile.city || "").split(",").map((part) => part.trim());
  const city = cityParts[0] || "";
  const state = profile.state || cityParts[1] || "";

  return {
    city,
    state: state.toUpperCase(),
  };
};

const toCityKey = (city: string, state: string) =>
  `${normalizeText(city).replace(/\s+/g, "-")}-${normalizeText(state)}`;

const toRad = (degrees: number) => (degrees * Math.PI) / 180;

const distanceBetween = (
  origin: ServiceAreaCoordinates,
  destination: ServiceAreaCoordinates,
) => {
  const earthRadiusKm = 6371;
  const dLat = toRad(destination.lat - origin.lat);
  const dLng = toRad(destination.lng - origin.lng);
  const lat1 = toRad(origin.lat);
  const lat2 = toRad(destination.lat);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return Number((earthRadiusKm * c).toFixed(1));
};

const getProfileOrigin = (
  profile: ServiceAreaProfile,
  curatedCity?: CuratedCity,
): ServiceAreaCoordinates | null => {
  if (
    Number.isFinite(profile.latitude) &&
    Number.isFinite(profile.longitude) &&
    profile.latitude !== undefined &&
    profile.longitude !== undefined
  ) {
    return {
      lat: profile.latitude,
      lng: profile.longitude,
    };
  }

  return curatedCity?.origin || null;
};

export const getServiceAreaOrigin = (profile: ServiceAreaProfile) => {
  const { city, state } = parseCityState(profile);
  if (!city) return null;

  return getProfileOrigin(profile, curatedAreas[toCityKey(city, state)]);
};

const curatedAreas: Record<string, CuratedCity> = {
  "aracaju-se": {
    origin: { lat: -10.9472, lng: -37.0731 },
    areas: [
      { label: "Centro, Aracaju - SE", lat: -10.913, lng: -37.0526 },
      { label: "Sao Jose, Aracaju - SE", lat: -10.9239, lng: -37.0656 },
      { label: "Treze de Julho, Aracaju - SE", lat: -10.9323, lng: -37.0448 },
      { label: "Jardins, Aracaju - SE", lat: -10.9426, lng: -37.0534 },
      { label: "Grageru, Aracaju - SE", lat: -10.9467, lng: -37.0645 },
      { label: "Luzia, Aracaju - SE", lat: -10.9519, lng: -37.0737 },
      { label: "Ponto Novo, Aracaju - SE", lat: -10.9337, lng: -37.0773 },
      { label: "Farolandia, Aracaju - SE", lat: -10.983, lng: -37.0643 },
      { label: "Atalaia, Aracaju - SE", lat: -10.9853, lng: -37.0508 },
      { label: "Coroa do Meio, Aracaju - SE", lat: -10.9656, lng: -37.0405 },
    ],
  },
  "barreiras-ba": {
    origin: { lat: -12.1528, lng: -44.99 },
    areas: [
      { label: "Centro, Barreiras - BA", lat: -12.1488, lng: -44.99 },
      { label: "Morada Nobre, Barreiras - BA", lat: -12.1364, lng: -45.0003 },
      { label: "Vila Rica, Barreiras - BA", lat: -12.1425, lng: -44.9792 },
      { label: "Jardim Ouro Branco, Barreiras - BA", lat: -12.1316, lng: -44.9836 },
      { label: "Renato Goncalves, Barreiras - BA", lat: -12.155, lng: -44.9819 },
      { label: "Santa Luzia, Barreiras - BA", lat: -12.1711, lng: -44.9923 },
      { label: "Sao Pedro, Barreiras - BA", lat: -12.1673, lng: -45.0034 },
      { label: "Vila Brasil, Barreiras - BA", lat: -12.1539, lng: -45.0159 },
      { label: "Boa Sorte, Barreiras - BA", lat: -12.1365, lng: -44.9653 },
      { label: "Loteamento Sao Paulo, Barreiras - BA", lat: -12.1278, lng: -44.9968 },
      { label: "Luis Eduardo Magalhaes - BA", distanceKm: 84.2 },
      { label: "Sao Desiderio - BA", distanceKm: 28.5 },
      { label: "Riachao das Neves - BA", distanceKm: 60.3 },
    ],
  },
  "florianopolis-sc": {
    origin: { lat: -27.5954, lng: -48.548 },
    areas: [
      { label: "Centro, Florianopolis - SC", lat: -27.5945, lng: -48.5482 },
      { label: "Agronomica, Florianopolis - SC", lat: -27.5764, lng: -48.5425 },
      { label: "Trindade, Florianopolis - SC", lat: -27.5866, lng: -48.5231 },
      { label: "Itacorubi, Florianopolis - SC", lat: -27.5839, lng: -48.505 },
      { label: "Santa Monica, Florianopolis - SC", lat: -27.5898, lng: -48.5123 },
      { label: "Coqueiros, Florianopolis - SC", lat: -27.6056, lng: -48.5824 },
      { label: "Estreito, Florianopolis - SC", lat: -27.5892, lng: -48.5786 },
      { label: "Saco dos Limoes, Florianopolis - SC", lat: -27.6117, lng: -48.533 },
      { label: "Pantanal, Florianopolis - SC", lat: -27.6081, lng: -48.5214 },
      { label: "Campeche, Florianopolis - SC", lat: -27.6816, lng: -48.4859 },
    ],
  },
  "goiania-go": {
    origin: { lat: -16.6869, lng: -49.2648 },
    areas: [
      { label: "Setor Central, Goiania - GO", lat: -16.6799, lng: -49.255 },
      { label: "Setor Sul, Goiania - GO", lat: -16.6893, lng: -49.2504 },
      { label: "Setor Oeste, Goiania - GO", lat: -16.6861, lng: -49.2789 },
      { label: "Setor Aeroporto, Goiania - GO", lat: -16.6745, lng: -49.2715 },
      { label: "Campinas, Goiania - GO", lat: -16.6698, lng: -49.2872 },
      { label: "Setor Bueno, Goiania - GO", lat: -16.7043, lng: -49.2768 },
      { label: "Setor Marista, Goiania - GO", lat: -16.6992, lng: -49.2639 },
      { label: "Vila Nova, Goiania - GO", lat: -16.6645, lng: -49.2501 },
      { label: "Jardim America, Goiania - GO", lat: -16.7195, lng: -49.2926 },
      { label: "Urias Magalhaes, Goiania - GO", lat: -16.6297, lng: -49.2733 },
      { label: "Jardim Curitiba, Goiania - GO", lat: -16.599, lng: -49.325 },
      { label: "Morada do Sol, Goiania - GO", lat: -16.6097, lng: -49.3522 },
      { label: "Aparecida de Goiania - GO", distanceKm: 17.1 },
      { label: "Senador Canedo - GO", distanceKm: 20.4 },
      { label: "Trindade - GO", distanceKm: 25.9 },
    ],
  },
  "santarem-pa": {
    origin: { lat: -2.4431, lng: -54.7081 },
    areas: [
      { label: "Centro, Santarem - PA", lat: -2.4385, lng: -54.6996 },
      { label: "Maracana, Santarem - PA", lat: -2.4599, lng: -54.7413 },
      { label: "Aeroporto Velho, Santarem - PA", lat: -2.4471, lng: -54.7223 },
      { label: "Santarenzinho, Santarem - PA", lat: -2.4553, lng: -54.7426 },
      { label: "Nova Republica, Santarem - PA", lat: -2.4928, lng: -54.7138 },
      { label: "Alter do Chao, Santarem - PA", distanceKm: 37.2 },
    ],
  },
};

const clampRadius = (radiusKm?: number) =>
  Math.max(2, Number(radiusKm) || 15);

const getAreaDistance = (
  area: CuratedArea,
  origin: ServiceAreaCoordinates | null,
) => {
  if (typeof area.distanceKm === "number") return area.distanceKm;
  if (origin && typeof area.lat === "number" && typeof area.lng === "number") {
    return distanceBetween(origin, { lat: area.lat, lng: area.lng });
  }

  return 5;
};

export const buildServiceAreaSuggestions = (
  profile: ServiceAreaProfile,
  options: ServiceAreaSuggestionOptions = {},
): ServiceAreaSuggestion[] => {
  const { city, state } = parseCityState(profile);
  if (!city) return [];

  const key = toCityKey(city, state);
  const curatedCity = curatedAreas[key];
  const origin = getProfileOrigin(profile, curatedCity);
  const existing = new Set(
    (profile.serviceAreas || []).map((area) => normalizeText(area)),
  );
  const query = normalizeText(options.query || "");
  const radiusKm = clampRadius(options.radiusKm);
  const fallbackAreas: CuratedArea[] = state
    ? [
        { distanceKm: 2, label: `Centro, ${city} - ${state}` },
        { distanceKm: 5, label: `${city} - ${state}` },
      ]
    : [
        { distanceKm: 2, label: `Centro, ${city}` },
        { distanceKm: 5, label: city },
      ];

  return (curatedCity?.areas || fallbackAreas)
    .map((area) => ({
      distanceKm: getAreaDistance(area, origin),
      label: area.label,
      source: curatedCity ? ("curated" as const) : ("fallback" as const),
    }))
    .filter((suggestion) => !existing.has(normalizeText(suggestion.label)))
    .filter((suggestion) => suggestion.distanceKm <= radiusKm)
    .filter((suggestion) =>
      query ? normalizeText(suggestion.label).includes(query) : true,
    )
    .sort((a, b) => a.distanceKm - b.distanceKm)
    .slice(0, options.limit || 14);
};
